// ============================================================
// Die Bausteine. Übernommen aus /opt/lilasp-zeitplan (dort aus fragenbuero,
// dort aus webgis) und auf die Maße der Entwurfsmappe gebracht.
//
// Die Regel dieser Datei: JEDE Größe kommt aus einer Klasse in base.css, nicht
// aus einem Inline-Stil. Nur so greift die Telefonfassung (44 px Tippfläche,
// 16 px Schriftgröße gegen den iOS-Zoom), die dort in einer Medienabfrage
// steht. Wer hier `height: 36` schreibt, hebelt sie aus.
//
// Die deutschen Namen sind Absicht (SPEC §1 für die Fachbegriffe), die
// englischen Bauteilnamen (`Button`, `Input`) ebenso: sie stehen in jedem
// Bildschirm hundertmal und sind der Teil, den man aus jedem React-Projekt
// kennt. Fachlich wird es Deutsch: `PhasenChip`, `Tage`, `Leer`, `Glas`.
// ============================================================
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import type { IconWeight } from "@phosphor-icons/react";
import type { Phase } from "../../api/types";
import { lesbar } from "../../lib/farben";
import { useT } from "../../lib/texte";
import { useUi } from "../../store/ui";
import { Icon } from "./Icon";

/** Wohin Modale, Auswahllisten und Meldungen gezeichnet werden. */
export function portalRoot(): HTMLElement {
  return document.getElementById("root") || document.body;
}

/* ------------------------------------------------------------------ Warten */

export function Spinner({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <span
      className="spin"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2px solid ${color || "var(--field-border)"}`,
        borderTopColor: color ? "transparent" : "var(--accent-text)",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}

/* ------------------------------------------------------------------ Knöpfe */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "subtle" | "danger";
  /** Name aus der Icon-Registry; steht links vom Text. */
  icon?: string;
  iconWeight?: IconWeight;
  /** sm 34 px (Werkzeugleisten) · md 36 px (Vorgabe) · lg 46 px (Absenden) */
  size?: "sm" | "md" | "lg";
}

/**
 * 36 px hoch, Radius 12 px, Gewicht 500 — die Werte der Entwurfsmappe.
 * `type` ist mit Absicht NICHT vorbelegt: ein Knopf in einem Formular ist
 * standardmäßig ein Absendeknopf, und genau das ist in den meisten Fällen
 * gemeint. Wer das nicht will, schreibt `type="button"`.
 */
export function Button({ variant = "primary", icon, iconWeight, children, className = "", size = "md", ...rest }: ButtonProps) {
  const groesse = size === "md" ? "" : ` btn-${size}`;
  return (
    <button className={`btn btn-${variant}${groesse} ring-focus ${className}`} {...rest}>
      {icon && <Icon name={icon} weight={iconWeight} size={size === "sm" ? 15 : 17} />}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  weight?: IconWeight;
  /** Kantenlänge des Symbols */
  size?: number;
  /** Kantenlänge der Trefferfläche; 36 px wie in der Mappe */
  box?: number;
  /** eingeschaltet — Akzentfläche statt durchsichtig */
  active?: boolean;
  /** umrandete Fassung: steht allein neben einer Überschrift */
  umrandet?: boolean;
}

/**
 * `title` wird AUCH zum `aria-label`: ein Knopf, der nur ein Symbol trägt,
 * heißt für eine Vorlesehilfe sonst „Schaltfläche" und sonst nichts.
 */
export function IconButton({ icon, weight, size = 18, box = 36, active, umrandet, title, className = "", style = {}, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`icon-btn ring-focus${umrandet ? " umrandet" : ""} ${className}`}
      style={{
        width: box,
        height: box,
        background: active ? "var(--accent-soft)" : undefined,
        color: active ? "var(--accent-text)" : undefined,
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} weight={weight} size={size} />
    </button>
  );
}

/* ------------------------------------------------------------------- Chips */

export interface ChipProps {
  children?: ReactNode;
  /** eingeschaltet (Akzentfläche, Akzentring, Akzenttext) */
  on?: boolean;
  /** Name aus der Icon-Registry, links vom Text */
  icon?: string;
  /** Punkt in dieser Farbe statt eines Symbols — die Bürofarbe der Mappe */
  punkt?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  /** mit Rückruf wird der Chip ein <button>, ohne bleibt er ein <span> */
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * 26 px hoch, Radius 999 px. Ein Chip OHNE `onClick` ist eine Plakette und
 * kein Knopf — er wird dann als `<span>` gezeichnet, damit die Tabulatorreise
 * nicht über zwei Dutzend Beschriftungen führt.
 */
export function Chip({ children, on, icon, punkt, title, className = "", style, onClick, disabled }: ChipProps) {
  const inhalt = (
    <>
      {punkt && <span className="dot" style={{ background: punkt }} />}
      {icon && <Icon name={icon} size={12} />}
      {children}
    </>
  );
  const klasse = `chip${on ? " on" : ""}${onClick ? " chip-btn ring-focus" : ""} ${className}`;
  if (!onClick) {
    return (
      <span className={klasse} title={title} style={style}>
        {inhalt}
      </span>
    );
  }
  return (
    <button type="button" className={klasse} title={title} aria-pressed={on} disabled={disabled} onClick={onClick} style={style}>
      {inhalt}
    </button>
  );
}

/* ------------------------------------------------------------------ Felder */

export function Field({ label, hint, children, className = "" }: { label?: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {label && <label className="lbl">{label}</label>}
      {children}
      {hint && <p className="hinweiszeile" style={{ marginTop: 6 }}>{hint}</p>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Name aus der Icon-Registry; das Symbol steht IM Feld, links */
  icon?: string;
  mono?: boolean;
  /**
   * Seit React 19 ist `ref` eine gewöhnliche Prop einer Funktionskomponente —
   * `forwardRef` braucht es dafür nicht mehr. Sie landet über ...rest ohnehin
   * am <input>; hier steht sie nur, damit der Typ sie zulässt. Ohne diese
   * Zeile scheitert jeder Aufrufer, der das Feld fokussieren will, an TS2322,
   * obwohl es zur Laufzeit funktioniert hätte.
   */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ icon, className = "", mono, style, ...rest }: InputProps) {
  if (icon)
    return (
      <div style={{ position: "relative" }}>
        <Icon
          name={icon}
          size={16}
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-mute)", pointerEvents: "none" }}
        />
        <input className={`inp ${mono ? "mono" : ""} ${className}`} style={{ paddingLeft: 36, ...style }} {...rest} />
      </div>
    );
  return <input className={`inp ${mono ? "mono" : ""} ${className}`} style={style} {...rest} />;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({ className = "", mono, rows = 4, style, ...rest }: TextareaProps) {
  return <textarea rows={rows} className={`inp ${mono ? "mono" : ""} ${className}`} style={style} {...rest} />;
}

/* ---------------------------------------------------------------- Auswahl */

/** Luft zwischen der aufgeklappten Liste und dem Fensterrand. */
const RAND = 8;

/** Obergrenze der Liste — darüber liest sich eine Zeile nicht mehr gut. */
const BREITESTE_LISTE = 460;

export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> {
  value: string | null | undefined;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  /** schmalerer Auslöser, damit er in einer Kopfzeile mitläuft */
  compact?: boolean;
  /** Filterfeld über der Liste; ab 12 Einträgen von selbst an */
  searchable?: boolean;
}

/**
 * Eigenes Auswahlfeld statt `<select>`: die aufgeklappte Liste eines nativen
 * `<select>` zeichnet das Betriebssystem, sie lässt sich weder runden noch
 * einfärben — und in dieser Oberfläche steht sie neben Chips und Knöpfen, die
 * beide gerundet sind. Die Schnittstelle ist die des nativen Feldes:
 * `onChange` bekommt weiterhin ein Ereignis der Form `{target: {value}}`.
 */
export function Select({
  value, onChange, options, className = "", placeholder, disabled, style, compact, searchable,
  // Die NAMENSGEBENDEN Attribute wandern auf den Auslöser-Knopf. `SelectProps`
  // erweitert `SelectHTMLAttributes`, der TYP nimmt sie also an — ohne diese
  // Zeilen fielen sie zur Laufzeit lautlos weg, und ein Auswahlfeld ohne Namen
  // heißt für eine Vorlesehilfe nur „…, Schaltfläche". Bewusst EINZELN
  // aufgezählt und nicht als `...rest` gespreizt: `SelectHTMLAttributes` trägt
  // Dinge wie `multiple`, `size` und `required`, die ein `<button>` nicht kennt.
  id, title, name,
  "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedby,
}: SelectProps) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number; auf: boolean } | null>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const { t } = useT();
  const aktuell = norm.find((o) => o.value === (value ?? ""));
  const mitSuche = searchable ?? norm.length > 12;
  const nadel = q.trim().toLowerCase();
  const sichtbar = nadel ? norm.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(nadel)) : norm;

  const platzieren = useCallback(() => {
    const r = knopfRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = Math.min(264, norm.length * 38 + 12) + (mitSuche ? 46 : 0);
    const auf = r.bottom + maxH + 8 > window.innerHeight && r.top - maxH - 8 > 0;
    setPos({ left: r.left, top: auf ? r.top - 4 : r.bottom + 4, width: r.width, auf });
  }, [norm.length, mitSuche]);

  useLayoutEffect(() => {
    if (offen) {
      setQ("");
      platzieren();
    }
  }, [offen, platzieren]);

  // Die Liste ist so breit wie ihr längster Eintrag (`max-content`), mindestens
  // so breit wie das Feld. Deshalb kann sie rechts hinausragen — dann rückt sie
  // nach links, so weit wie nötig und nie über den linken Rand hinaus. Erst
  // NACH dem Zeichnen, weil vorher niemand weiß, wie breit sie wird.
  useLayoutEffect(() => {
    const el = listeRef.current;
    if (!offen || !el || !pos) return;
    const b = el.getBoundingClientRect();
    const zuviel = b.right - (window.innerWidth - RAND);
    if (zuviel <= 0.5) return;
    const links = Math.max(RAND, pos.left - zuviel);
    // Die halbe Pixelbreite hält die Schleife an: ohne sie liefe das Rücken
    // ewig weiter, weil `getBoundingClientRect` gebrochene Werte liefert.
    if (Math.abs(links - pos.left) > 0.5) setPos({ ...pos, left: links });
  }, [offen, pos]);

  useEffect(() => {
    if (!offen) return;
    // Schließen nur bei Klick daneben oder Escape — Rollen und Größenänderung
    // RÜCKEN bloß nach, damit die Liste am Feld klebt (wie ein natives
    // <select>), statt zuzuschnappen.
    const beiDruck = (e: Event) => {
      if (listeRef.current?.contains(e.target as Node) || knopfRef.current?.contains(e.target as Node)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Die offene Liste verbraucht das Escape VOLLSTÄNDIG — sonst schlösse
      // dasselbe Escape auch ein Modal darüber und würfe dessen Eingaben weg.
      e.stopPropagation();
      setQ((cur) => {
        if (cur) return "";
        setOffen(false);
        return cur;
      });
    };
    const beiRollen = (e: Event) => {
      if (listeRef.current?.contains(e.target as Node)) return;
      platzieren();
    };
    document.addEventListener("mousedown", beiDruck);
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("scroll", beiRollen, true);
    window.addEventListener("resize", platzieren);
    return () => {
      document.removeEventListener("mousedown", beiDruck);
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("scroll", beiRollen, true);
      window.removeEventListener("resize", platzieren);
    };
  }, [offen, platzieren]);

  const waehlen = (v: string) => {
    onChange({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>);
    setOffen(false);
    knopfRef.current?.focus();
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <button
        ref={knopfRef}
        type="button"
        disabled={disabled}
        className={`inp${compact ? " inp-compact" : ""} ${className}`}
        id={id}
        // Im Feld selbst bleibt es beim Abschneiden — dafür ist es zu schmal.
        // Der gewählte Eintrag steht aber im Tooltip, wenn nicht ohnehin ein
        // eigener gesetzt ist.
        title={title ?? aktuell?.label}
        name={name}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-haspopup="listbox"
        aria-expanded={offen}
        onClick={() => !disabled && setOffen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: aktuell ? "var(--text)" : "var(--text-mute)" }}>
          {aktuell?.label ?? placeholder ?? ""}
        </span>
        <Icon name="caret-down" size={14} style={{ color: "var(--text-mute)", flex: "none", transition: "transform .15s", transform: offen ? "rotate(180deg)" : "none" }} />
      </button>
      {offen &&
        pos &&
        createPortal(
          <div
            ref={listeRef}
            className="scroll anim-pop"
            role="listbox"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.auf ? undefined : pos.top,
              bottom: pos.auf ? window.innerHeight - pos.top : undefined,
              // Nicht `width: pos.width`: dann schnitte ein schmales Feld jeden
              // längeren Eintrag ab („Landschaftspflegerischer Beglei…"). Das
              // Feld gibt nur noch das MINDESTmaß; gewachsen wird nach Inhalt.
              minWidth: pos.width,
              width: "max-content",
              maxWidth: Math.min(BREITESTE_LISTE, window.innerWidth - 2 * RAND),
              maxHeight: 264,
              overflowY: "auto",
              borderRadius: "var(--r-knopf)",
              padding: 5,
              zIndex: 1000,
              background: "var(--panel-solid)",
              border: "1px solid var(--glass-border-strong)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {mitSuche && (
              <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "1px 1px 6px", background: "var(--panel-solid)" }}>
                <Input
                  icon="magnifying-glass"
                  autoFocus
                  value={q}
                  placeholder={t.allgemein.suchen}
                  aria-label={t.allgemein.suchen}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter auf einem eindeutigen Treffer wählt ihn direkt.
                    const eins = sichtbar[0];
                    if (e.key === "Enter" && sichtbar.length === 1 && eins) waehlen(eins.value);
                  }}
                  style={{ padding: "7px 10px 7px 34px", fontSize: 13, borderRadius: "var(--r-knopf)", height: 34 }}
                />
              </div>
            )}
            {mitSuche && sichtbar.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--text-mute)" }}>{t.ortssuche.keineTreffer}</div>
            )}
            {sichtbar.map((o) => {
              const an = o.value === (value ?? "");
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={an}
                  onClick={() => waehlen(o.value)}
                  className="wahlzeile"
                  style={{
                    fontWeight: an ? 600 : 500,
                    background: an ? "var(--accent-soft)" : "transparent",
                    color: an ? "var(--accent-text)" : "var(--text-dim)",
                  }}
                >
                  {an ? <Icon name="check" size={13} /> : <span style={{ width: 13, flex: "none" }} />}
                  {/* `1 1 auto` statt `1`: die Basis ist der Text selbst, nicht
                      0. Chrome misst `max-content` nachgeprüft in BEIDEN
                      Fassungen gleich — die Liste wird also so oder so breit
                      genug. Die Basis am Text steht hier, weil sie beim
                      Ausmessen keine Auslegungssache ist; ausprobiert ist das
                      aber nur in Chrome. Gewachsen wird wie vorher. */}
                  <span style={{ flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </button>
              );
            })}
          </div>,
          portalRoot(),
        )}
    </div>
  );
}

/* ---------------------------------------------------------------- Schalter */

export function Switch({ on, onChange, disabled, label }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      className={`io ${on ? "on" : ""} ring-focus`}
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!on)}
      style={{ opacity: disabled ? 0.5 : 1, border: 0, padding: 0 }}
    >
      <span className="knob" />
    </button>
  );
}

/* ------------------------------------------------------------------ Avatar */

/**
 * Das Wenige, das ein Avatar von einer Person wissen muss.
 *
 * Ausgeschrieben und NICHT als `Pick<Person, …>`: `Ich.anzeigename` ist
 * `string | null` (eine frisch angelegte Person hat noch keinen), `Person.
 * anzeigename` dagegen `string` — der Server setzt dort den Ortsteil der
 * Adresse ein. Ein `Pick` von `Person` nähme `Ich` deshalb nicht an, und die
 * Kopfleiste zeichnet genau `Ich`.
 */
export interface AvatarPerson {
  anzeigename: string | null;
  kuerzel: string;
  avatar_url: string | null;
  buero_id: string | null;
}

export interface AvatarProps {
  person: AvatarPerson;
  size?: number;
  /** Ring in der Farbe des Heimatbüros (E6/E15) */
  ring?: boolean;
  /** Ringfarbe von Hand — sonst kommt sie aus `useUi().bueroFarben` */
  farbe?: string;
  title?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

/**
 * Kreis mit Profilbild oder Initialen.
 *
 * Die RINGFARBE kommt aus der Farbtafel im Oberflächenspeicher und NICHT aus
 * einer eigenen Abfrage: derselbe Avatar steht in der Kopfleiste, auf der
 * Karte, im Board und in der Matrix — vierzig Haken auf `/stand` für eine
 * Farbe wären vierzig Abonnements zu viel, und abgemeldet gibt es `/stand`
 * gar nicht.
 *
 * Der Ring liegt als DOPPELTER box-shadow außen an (2 px Bürofarbe, danach
 * 1,5 px Grundfarbe), damit er das Bild nicht beschneidet und auch vor einem
 * gleichfarbigen Untergrund steht.
 */
export function Avatar({ person, size = 30, ring, farbe, title, style, onClick }: AvatarProps) {
  const ausTafel = useUi((s) => (person.buero_id ? s.bueroFarben[person.buero_id] : undefined));
  const ringfarbe = farbe ?? ausTafel ?? "var(--accent-2)";
  // Ein Name muss es sein: eine Vorlesehilfe liest sonst „Grafik" und sonst
  // nichts. Ohne Anzeigenamen (frisch angelegte Person) steht das Kürzel da.
  const name = title ?? person.anzeigename ?? person.kuerzel;
  const gemeinsam: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.37),
    boxShadow: ring ? `0 0 0 2px ${ringfarbe}, 0 0 0 3.5px var(--panel-solid)` : undefined,
    cursor: onClick ? "pointer" : undefined,
    ...style,
  };
  const inhalt = person.avatar_url ? (
    // `alt=""` und der Name am Behälter: sonst liest eine Vorlesehilfe den
    // Namen zweimal — einmal als Bildbeschreibung, einmal als Titel.
    <img src={person.avatar_url} alt="" loading="lazy" decoding="async" />
  ) : (
    person.kuerzel
  );
  if (onClick) {
    return (
      <button type="button" className="av ring-focus" style={{ ...gemeinsam, border: 0, padding: 0 }} title={name} aria-label={name} onClick={onClick}>
        {inhalt}
      </button>
    );
  }
  return (
    <span className="av" style={gemeinsam} title={name} role="img" aria-label={name}>
      {inhalt}
    </span>
  );
}

/* ----------------------------------------------------------- Phasenplakette */

/**
 * Die Kurzform einer Phase auf ihrer Farbe („LPH 3", „WB", „BET").
 *
 * Die Schriftfarbe wird GERECHNET und nicht gesetzt: die Phasenfarben der
 * Entwurfsmappe sind hell und tragen dunkle Schrift, aber ein Admin darf
 * umfärben (E10) — bei einer dunklen Phase kippt `lesbar()` auf Weiß, statt
 * unleserlich zu bleiben.
 */
export function PhasenChip({
  phase,
  titel,
  style,
  text,
}: {
  phase: Phase;
  titel?: string;
  style?: CSSProperties;
  /** Statt des Kürzels — für die Konzeptphasen, deren Kürzel („ANA", „BET")
   *  ohne die Bezeichnung niemand liest. */
  text?: string;
}) {
  return (
    <span className="ph" title={titel} style={{ background: phase.farbe, color: lesbar(phase.farbe), ...style }}>
      {/* `||`, nicht `??`: `name_en` ist bei vielen Phasen der leere String,
          und eine namenlose farbige Pastille sagt nichts. */}
      {text || phase.kurz}
    </span>
  );
}

/**
 * Der Fortschrittsbalken eines KONZEPTPROJEKTS (007, SPEC §12.1): wie viele
 * seiner Bestandteile abgehakt sind.
 *
 * Er steht an der Stelle, an der bei einer Objektplanung der `PhasenChip`
 * steht — auf der Galeriekachel, auf der Brettkarte und im Kopf der Akte.
 * `farbe` ist die BUEROFARBE (SPEC §12.3) und kein Vorgabewert: der Balken
 * gehört zum selben Farbfaden wie Stift, Bogen und Kachelkante.
 *
 * `gesamt === 0` zeichnet NICHTS. Ein leerer Balken bei „noch keine
 * Bestandteile" sähe aus wie „nichts geschafft" und nicht wie „noch nichts
 * aufgeschrieben" — zwei verschiedene Dinge.
 */
export function KonzeptBalken({
  erledigt,
  inArbeit = 0,
  gesamt,
  farbe,
  breit = false,
  titel,
}: {
  erledigt: number;
  /** §40.3: was gerade in Bearbeitung ist — schraffiert hinter dem Erledigten. */
  inArbeit?: number;
  gesamt: number;
  farbe: string;
  /** Kachel und Brettkarte: über die ganze Breite, ohne Zahl. */
  breit?: boolean;
  titel?: string;
}) {
  if (gesamt <= 0) return null;
  const anteil = Math.max(0, Math.min(1, erledigt / gesamt));
  return (
    <span
      className={`kb${breit ? " kb-breit" : ""}`}
      title={titel}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={gesamt}
      aria-valuenow={erledigt}
      aria-label={titel}
      style={{ "--kb-farbe": farbe } as CSSProperties}
    >
      <span className="kb-spur">
        {inArbeit > 0 && (
          <span
            className="kb-arbeit"
            style={{ width: `${Math.min(1, (erledigt + inArbeit) / gesamt) * 100}%` }}
          />
        )}
        {erledigt > 0 && <span className="kb-fuell" style={{ width: `${anteil * 100}%` }} />}
      </span>
      {breit ? null : <span className="mono kb-zahl">{erledigt}/{gesamt}</span>}
    </span>
  );
}

/* --------------------------------------------------------------- Countdown */

/**
 * „in 4 T" · „heute" · „vor 3 T" — die Plakette, die auf jeder Projektkarte,
 * in der Zeitleiste und auf der Karte steht.
 *
 * Drei Stufen, und die mittlere ist die aus der Mappe:
 *   überfällig (n < 0)   Fehlerfarbe
 *   bald (0 … 7 Tage)    Warnfarbe — dieselbe Schwelle wie der Pulsring (E11)
 *   später               gedämpft
 * `n === null` (unlesbares Datum aus `tageBis`) zeichnet NICHTS, statt „NaN T".
 */
export function Tage({ n, style }: { n: number | null | undefined; style?: CSSProperties }) {
  const { t } = useT();
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  const farbe = n < 0 ? "var(--error)" : n <= 7 ? "var(--warn)" : "var(--text-mute)";
  const text = n === 0 ? t.tage.heute : n === 1 ? t.tage.morgen : n === -1 ? t.tage.gestern : n > 0 ? t.tage.in(n) : t.tage.vor(-n);
  return (
    <span className="mono tnum" style={{ color: farbe, fontWeight: n <= 7 ? 500 : 400, whiteSpace: "nowrap", ...style }}>
      {text}
    </span>
  );
}

/* ------------------------------------------------------------- Leerzustand */

export function Leer({ icon, title, sub, aktion }: { icon: string; title: string; sub?: string; aktion?: ReactNode }) {
  return (
    <div className="leer">
      <Icon name={icon} weight="light" size={46} style={{ opacity: 0.5, margin: "0 auto" }} />
      <p className="leer-titel">{title}</p>
      {sub && <p style={{ fontSize: 13, marginTop: 5 }}>{sub}</p>}
      {aktion && <div className="reihe wrap" style={{ justifyContent: "center", marginTop: 18 }}>{aktion}</div>}
    </div>
  );
}

/* --------------------------------------------------------------- Glasfläche */

export interface GlasProps {
  children: ReactNode;
  /** deckender und stärker verwischt — über Bildern und auf dem Telefon */
  stark?: boolean;
  /** Radius 20 px ist die Vorgabe; Werkzeugleisten der Mappe nehmen 16 px */
  radius?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Die schwebende Fläche über der Karte (Kopfleiste, Projektkarte, Werkzeuge,
 * Filterleiste). Ein `<div>` mit `.glass` — der Baustein ist trotzdem seinen
 * Namen wert, weil `stark` und `radius` sonst an zwanzig Stellen als
 * Inline-Stil stünden und auseinanderliefen.
 */
export function Glas({ children, stark, radius, className = "", style }: GlasProps) {
  return (
    <div className={`${stark ? "glass-strong" : "glass"} ${className}`} style={radius === undefined ? style : { borderRadius: radius, ...style }}>
      {children}
    </div>
  );
}
